import { formatDate } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from 'common/services/auth.service';
import {
  RealtimeService,
  RealtimeStatus,
} from 'common/services/realtime.service';
import { Configuration } from 'config/config';

interface LiveAttendanceMember {
  customerId?: string;
  name?: string;
  tagNumber?: number;
  profile?: string;
  packageName?: string;
  gender?: string;
  status: string;
  punchTime: string;
  branchId: string;
  dueDate?: string;
  areaName?: string;
  punchState?: string;
}

interface VisibleCard {
  key: string;
  member: LiveAttendanceMember;
  leaving: boolean;
  paused: boolean;
}

interface MemberStatusView {
  label: string;
  icon: string;
}

const CHANNEL = 'attendance';
const PUNCH_EVENT = 'attendance.punch';
/** How long each punch stays on screen. */
const DISPLAY_MS = 5 * 1000;
/** Matches the leave animation before the card is removed from the grid. */
const SWITCH_MS = 450;
/** How long cards take to glide into their new grid slot when the count changes. */
const REFLOW_MS = 550;
const MAX_VISIBLE = 4;

const MEMBER_STATUS: Record<string, MemberStatusView> = {
  active: { label: 'Active', icon: 'fa-check-circle' },
  defaulter: { label: 'Defaulter', icon: 'fa-exclamation-circle' },
  freeze: { label: 'Frozen', icon: 'fa-snowflake-o' },
  unauthorized: { label: 'Unauthorized', icon: 'fa-exclamation-triangle' },
};

const MALE_AVATAR = 'assets/layout/images/avatarBoyImage.png';
const FEMALE_AVATAR = 'assets/layout/images/avatarGirlImage.png';

const STATUS_VOICE: Record<string, string> = {
  active: 'assets/live-attendance/active.mp3',
  defaulter: 'assets/live-attendance/defaulter.mp3',
  freeze: 'assets/live-attendance/frozen.mp3',
  unauthorized: 'assets/live-attendance/unauthorized.mp3',
};

@Component({
  selector: 'app-live-attendance-screen',
  templateUrl: './live-attendance-screen.component.html',
  styleUrls: ['./live-attendance-screen.component.css'],
  providers: [RealtimeService],
})
export class LiveAttendanceScreenComponent implements OnInit, OnDestroy {
  visible: VisibleCard[] = [];
  queue: Array<Pick<VisibleCard, 'key' | 'member'>> = [];
  status: RealtimeStatus = 'idle';
  permitted = true;
  branchName = '';
  branchLogo: string | null = null;
  private expireMemberPriorOneDay = false;
  readonly displayMs = DISPLAY_MS;

  private statusSub?: Subscription;
  private subscribed = false;
  private readonly hideTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  /** Epoch ms when the card's current timer (display or leave) should fire. */
  private readonly deadlines = new Map<string, number>();
  /** Time left when a card is paused by the cursor. */
  private readonly remainingMs = new Map<string, number>();
  private readonly reflows = new WeakMap<HTMLElement, Animation>();
  private readonly voices: HTMLAudioElement[] = [];
  private readonly unlockAudio = (): void => {
    document.removeEventListener('pointerdown', this.unlockAudio);
    const audio = new Audio(STATUS_VOICE['active']);
    audio.volume = 0;
    void audio.play().then(() => audio.pause()).catch(() => undefined);
  };

  constructor(
    private readonly realtime: RealtimeService,
    public readonly config: Configuration,
    private readonly host: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const branch = AuthService.getLoggedInCurrentBranch();
    this.branchName = branch?.name ?? '';
    this.expireMemberPriorOneDay = !!branch?.permissions?.expireMemberPriorOneDay;
    const logo = branch?.companyDetails?.bannerImage?.image;
    this.branchLogo = logo
      ? `${this.config.Server}${this.config.branchImagePath}${logo}`
      : null;

    if (!branch?.permissions?.liveAttendanceScreen) {
      this.permitted = false;
      return;
    }
    document.addEventListener('pointerdown', this.unlockAudio);
    const branchId = AuthService.getLoggedInBranch();

    this.statusSub = this.realtime.status.subscribe((status) => {
      this.status = status;
      if (status === 'connected' && !this.subscribed) {
        this.subscribed = true;
        void this.realtime.subscribe(CHANNEL, branchId);
      }
    });

    this.realtime.connect(branchId);
    this.realtime.on<LiveAttendanceMember>(PUNCH_EVENT, (member) =>
      this.onPunch(member),
    );
  }

  ngOnDestroy(): void {
    document.removeEventListener('pointerdown', this.unlockAudio);
    this.statusSub?.unsubscribe();
    this.hideTimers.forEach((timer) => clearTimeout(timer));
    this.hideTimers.clear();
    this.deadlines.clear();
    this.remainingMs.clear();
    this.queue = [];
    this.stopVoices();
    this.realtime.disconnect();
  }

  get statusLabel(): string {
    switch (this.status) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting…';
      case 'reconnecting':
        return 'Reconnecting…';
      case 'denied':
        return 'Access denied';
      case 'disconnected':
        return 'Disconnected';
      default:
        return 'Idle';
    }
  }

  memberImage(member: LiveAttendanceMember): string {
    if (!member.profile) {
      return this.fallbackAvatar(member);
    }
    if (member.profile.startsWith('assets/')) {
      return member.profile;
    }
    return `${this.config.Server}${this.config.memberImagePath}${member.profile}`;
  }

  onBranchLogoError(): void {
    this.branchLogo = null;
  }

  onImageError(event: Event, member: LiveAttendanceMember): void {
    const img = event.target as HTMLImageElement;
    const fallback = this.fallbackAvatar(member);
    if (!img.src.endsWith(fallback)) {
      img.src = fallback;
    }
  }

  genderLabel(member: LiveAttendanceMember): string {
    const gender = (member.gender ?? '').trim().toUpperCase();
    if (gender === 'M' || gender === 'MALE') {
      return 'Male';
    }
    if (gender === 'F' || gender === 'FEMALE') {
      return 'Female';
    }
    return member.gender || '—';
  }

  memberStatus(member: LiveAttendanceMember): MemberStatusView {
    return (
      MEMBER_STATUS[member.status] ?? {
        label: member.status,
        icon: 'fa-info-circle',
      }
    );
  }

  displayName(member: LiveAttendanceMember): string {
    const name = member.name?.trim();
    if (name) {
      return name;
    }
    if (member.status === 'unauthorized') {
      return 'Unknown Member';
    }
    return '—';
  }

  dueDateLabel(value: string | undefined): string {
    if (!value) {
      return '';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return formatDate(parsed, 'd MMM y', 'en-US');
  }

  kickerLabel(member: LiveAttendanceMember): string {
    if (member.punchState) {
      return member.punchState;
    }
    return member.status === 'unauthorized' ? 'Attention' : 'Welcome';
  }

  expiryTone(value: string | undefined): string {
    const days = this.daysUntil(value);
    if (days == null) {
      return 'none';
    }
    if (days < 0) {
      return 'expired';
    }
    return days <= 7 ? 'soon' : 'ok';
  }

  packageExpiryLabel(value: string | undefined): string {
    const days = this.daysUntil(value);
    if (days == null) {
      return '';
    }
    if (days === 0) {
      return this.expireMemberPriorOneDay
        ? 'Package has expired today'
        : 'Package expires today';
    }
    if (days > 0) {
      return `Package will expire in ${days} ${days === 1 ? 'Day' : 'Days'}`;
    }
    const ago = Math.abs(days);
    return `Package has expired ${ago} ${ago === 1 ? 'day' : 'days'} ago`;
  }

  /** Whole calendar days from today to the due date; negative once it has passed. */
  private daysUntil(value: string | undefined): number | null {
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    const due = new Date(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate(),
    );
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((due.getTime() - today.getTime()) / 86400000);
  }

  pauseCard(key: string): void {
    const card = this.visible.find((item) => item.key === key);
    if (!card || card.paused) {
      return;
    }
    const deadline = this.deadlines.get(key) ?? Date.now();
    this.remainingMs.set(key, Math.max(0, deadline - Date.now()));
    this.clearTimer(key);
    this.visible = this.visible.map((item) =>
      item.key === key ? { ...item, paused: true } : item,
    );
  }

  resumeCard(key: string): void {
    const card = this.visible.find((item) => item.key === key);
    if (!card?.paused) {
      return;
    }
    const remaining = this.remainingMs.get(key) ?? 0;
    this.remainingMs.delete(key);
    this.visible = this.visible.map((item) =>
      item.key === key ? { ...item, paused: false } : item,
    );
    this.armTimer(
      key,
      remaining,
      card.leaving ? () => this.remove(key) : () => this.beginLeave(key),
    );
  }

  trackByCard(_index: number, card: VisibleCard): string {
    return card.key;
  }

  private fallbackAvatar(member: LiveAttendanceMember): string {
    return this.genderLabel(member) === 'Female' ? FEMALE_AVATAR : MALE_AVATAR;
  }

  private onPunch(member: LiveAttendanceMember): void {
    if (!member?.punchTime || !member?.branchId) {
      return;
    }

    const identity = member.customerId;
    if (identity) {
      const onScreen = this.visible.find(
        (card) => card.member.customerId === identity,
      );
      if (onScreen && !onScreen.leaving) {
        this.visible = this.visible.map((card) =>
          card.member.customerId === identity ? { ...card, member } : card,
        );
        return;
      }
      if (onScreen?.leaving) {
        clearTimeout(this.hideTimers.get(onScreen.key));
        this.hideTimers.delete(onScreen.key);
        this.updateCards(() => {
          this.visible = this.visible.filter((card) => card.key !== onScreen.key);
          this.show(onScreen.key, member);
        });
        return;
      }

      const queuedIndex = this.queue.findIndex(
        (item) => item.member.customerId === identity,
      );
      if (queuedIndex !== -1) {
        this.queue = this.queue.map((item, index) =>
          index === queuedIndex ? { ...item, member } : item,
        );
        return;
      }
    }

    const key =
      identity ||
      `punch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    if (this.visible.length >= MAX_VISIBLE) {
      this.queue = [...this.queue, { key, member }];
      return;
    }
    this.updateCards(() => this.show(key, member));
  }

  private show(key: string, member: LiveAttendanceMember): void {
    this.visible = [
      ...this.visible,
      { key, member, leaving: false, paused: false },
    ];
    this.playVoice(member.status);
    this.armTimer(key, DISPLAY_MS, () => this.beginLeave(key));
  }

  private playVoice(status: string): void {
    const src = STATUS_VOICE[status];
    if (!src) {
      return;
    }
    this.stopVoices();
    const audio = new Audio(src);
    this.voices.push(audio);
    audio.addEventListener('ended', () => {
      const index = this.voices.indexOf(audio);
      if (index !== -1) {
        this.voices.splice(index, 1);
      }
    });
    void audio.play().catch(() => undefined);
  }

  private stopVoices(): void {
    this.voices.forEach((audio) => {
      audio.pause();
      audio.src = '';
    });
    this.voices.length = 0;
  }

  private beginLeave(key: string): void {
    const card = this.visible.find((item) => item.key === key);
    if (!card || card.paused) {
      return;
    }
    this.visible = this.visible.map((item) =>
      item.key === key ? { ...item, leaving: true } : item,
    );
    this.armTimer(key, SWITCH_MS, () => this.remove(key));
  }

  private remove(key: string): void {
    this.clearTimer(key);
    this.remainingMs.delete(key);
    this.updateCards(() => {
      this.visible = this.visible.filter((card) => card.key !== key);
      this.fillFromQueue();
    });
  }

  private armTimer(key: string, ms: number, fire: () => void): void {
    this.clearTimer(key);
    this.deadlines.set(key, Date.now() + ms);
    this.hideTimers.set(key, setTimeout(fire, ms));
  }

  private clearTimer(key: string): void {
    const timer = this.hideTimers.get(key);
    if (timer) {
      clearTimeout(timer);
    }
    this.hideTimers.delete(key);
    this.deadlines.delete(key);
  }

  private fillFromQueue(): void {
    const next: Array<Pick<VisibleCard, 'key' | 'member'>> = [];
    while (
      this.queue.length > 0 &&
      this.visible.length + next.length < MAX_VISIBLE
    ) {
      const card = this.queue[0];
      this.queue = this.queue.slice(1);
      next.push(card);
    }
    next.forEach((card) => this.show(card.key, card.member));
  }

  /**
   * Applies a change to the visible cards, then slides every card that stays on
   * screen from where it was to its new grid slot instead of letting it jump.
   */
  private updateCards(change: () => void): void {
    const before = new Map<string, DOMRect>();
    for (const el of this.cardElements()) {
      before.set(el.dataset['id'] ?? '', el.getBoundingClientRect());
    }

    change();
    this.cdr.detectChanges();

    const grid = this.host.nativeElement.querySelector<HTMLElement>('.la-grid');
    if (!grid || before.size === 0) {
      return;
    }
    // Target slots come from offsets, which ignore transforms, so a card that
    // is still mid-animation is measured at the slot it is heading to.
    const gridBox = grid.getBoundingClientRect();

    for (const el of this.cardElements()) {
      const from = before.get(el.dataset['id'] ?? '');
      if (!from) {
        continue;
      }
      const width = el.offsetWidth;
      const height = el.offsetHeight;
      const dx =
        from.left + from.width / 2 - (gridBox.left + el.offsetLeft + width / 2);
      const dy =
        from.top + from.height / 2 - (gridBox.top + el.offsetTop + height / 2);
      const resized =
        Math.abs(from.width - width) > 1 || Math.abs(from.height - height) > 1;
      if (!resized && Math.abs(dx) < 1 && Math.abs(dy) < 1) {
        continue;
      }

      // Uniform scale keeps the photo round while the card changes shape.
      const scale = Math.min(from.width / width, from.height / height);
      const start: Keyframe = {
        transform: `translate(${dx}px, ${dy}px) scale(${scale})`,
      };
      const end: Keyframe = { transform: 'none' };
      if (resized && !el.classList.contains('la-card--out')) {
        start['opacity'] = 0.35;
        end['opacity'] = 1;
      }

      this.reflows.get(el)?.cancel();
      this.reflows.set(
        el,
        el.animate([start, end], {
          duration: REFLOW_MS,
          easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
        }),
      );
    }
  }

  private cardElements(): HTMLElement[] {
    return Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('.la-card'),
    );
  }
}
